import { useCallback, useState } from 'react'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { InteractionStatus } from '@azure/msal-browser'
import { loginRequest } from '@/config/authConfig'

export type AuthFlowStatus = 'initializing' | 'unauthenticated' | 'authenticating' | 'authenticated' | 'error'

interface UseAuthFlowResult {
  status: AuthFlowStatus
  errorMessage: string | null
  signIn: () => Promise<void>
  clearError: () => void
}

/**
 * Thin orchestration layer over msal-react for the interactive sign-in
 * experience. Keeps the popup flow's loading/error state explicit so the
 * UI never shows a blank page.
 */
export function useAuthFlow(): UseAuthFlowResult {
  const { instance, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [signingIn, setSigningIn] = useState(false)

  const signIn = useCallback(async () => {
    setErrorMessage(null)
    setSigningIn(true)
    try {
      // Full-page redirect flow: more reliable than popups for SPAs (avoids
      // popup blockers, nested-popup and monitor-timeout errors). The page
      // navigates to Entra and back; handleRedirectPromise (initializeMsal)
      // completes the sign-in on return.
      await instance.loginRedirect(loginRequest)
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Autentificarea cu Microsoft Entra a eșuat. Încearcă din nou.',
      )
      setSigningIn(false)
    }
  }, [instance])

  const clearError = useCallback(() => setErrorMessage(null), [])

  let status: AuthFlowStatus
  if (inProgress === InteractionStatus.Startup || inProgress === InteractionStatus.HandleRedirect) {
    status = 'initializing'
  } else if (errorMessage) {
    status = 'error'
  } else if (isAuthenticated) {
    status = 'authenticated'
  } else if (signingIn || inProgress === InteractionStatus.AcquireToken) {
    status = 'authenticating'
  } else {
    status = 'unauthenticated'
  }

  return { status, errorMessage, signIn, clearError }
}
